import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  BookOpen, ArrowLeft, ChevronRight, Search, 
  Layers, Database, Cloud, Shield, Cpu, GitBranch,
  CheckCircle, Star, ExternalLink, Play
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import Footer from "@/components/Footer";
import { MarkdownContent } from "@/components/MarkdownContent";
import {
  CleanArchitectureDiagram,
  SagaPatternDiagram,
  CircuitBreakerDiagram,
  ShardingDiagram,
  ApiGatewayDiagram,
  BoundedContextDiagram,
  LoadBalancingDiagram
} from "@/components/ArchitectureDiagrams";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Map topic IDs to their diagram components
const TOPIC_DIAGRAMS = {
  'dependency-rule': CleanArchitectureDiagram,
  'saga-pattern': SagaPatternDiagram,
  'circuit-breaker': CircuitBreakerDiagram,
  'database-scaling': ShardingDiagram,
  'api-gateway': ApiGatewayDiagram,
  'bounded-context': BoundedContextDiagram,
  'load-balancing': LoadBalancingDiagram,
};

// Architecture knowledge categories
const LEARNING_CATEGORIES = [
  {
    id: 'clean-architecture',
    title: 'Clean Architecture',
    icon: Layers,
    color: 'from-blue-500 to-cyan-500',
    description: 'Принципи чистої архітектури від Роберта Мартіна',
    topics: [
      {
        id: 'solid',
        title: 'SOLID Principles',
        content: `
# SOLID Principles

SOLID — це акронім для п'яти принципів об'єктно-орієнтованого програмування:

## S — Single Responsibility Principle (SRP)
Клас повинен мати лише одну причину для зміни. Кожен модуль чи клас повинен відповідати за одну частину функціональності програми.

**Приклад:** Замість одного класу UserManager, що обробляє реєстрацію, авторизацію і профілі, створіть окремі класи для кожної відповідальності.

## O — Open/Closed Principle (OCP)
Програмні сутності повинні бути відкриті для розширення, але закриті для модифікації.

**Приклад:** Використовуйте інтерфейси та абстрактні класи для додавання нової функціональності без зміни існуючого коду.

## L — Liskov Substitution Principle (LSP)
Об'єкти суперкласу повинні бути замінюваними об'єктами підкласів без порушення коректності програми.

**Приклад:** Якщо у вас є клас Bird з методом fly(), то клас Penguin не повинен наслідувати Bird, оскільки пінгвіни не літають.

## I — Interface Segregation Principle (ISP)
Клієнти не повинні залежати від інтерфейсів, які вони не використовують.

**Приклад:** Замість одного великого інтерфейса IWorker з методами work(), eat(), sleep(), створіть окремі інтерфейси.

## D — Dependency Inversion Principle (DIP)
Модулі високого рівня не повинні залежати від модулів низького рівня. Обидва повинні залежати від абстракцій.

**Приклад:** Використовуйте Dependency Injection для передачі залежностей замість створення їх всередині класу.
        `,
        book: 'Clean Architecture - Robert C. Martin'
      },
      {
        id: 'dependency-rule',
        title: 'The Dependency Rule',
        content: `
# The Dependency Rule

Головне правило Clean Architecture: **залежності коду завжди спрямовані всередину**.

## Концентричні кола архітектури

### 1. Entities (Сутності)
- Бізнес-правила підприємства
- Найвнутрішній рівень
- Не залежать ні від чого
- Найменш схильні до змін

### 2. Use Cases (Варіанти використання)
- Бізнес-правила додатку
- Оркеструють потік даних до/від Entities
- Не знають про UI, БД, або зовнішні агентства

### 3. Interface Adapters (Адаптери інтерфейсів)
- Перетворюють дані між Use Cases і зовнішнім світом
- Controllers, Presenters, Gateways
- Зберігають Use Cases чистими від зовнішніх деталей

### 4. Frameworks & Drivers (Фреймворки і драйвери)
- Найзовнішній рівень
- База даних, UI фреймворки, веб-сервери
- "Клей" для внутрішніх шарів

## Ключові переваги:
- Незалежність від UI
- Незалежність від бази даних
- Незалежність від зовнішніх агентств
- Тестованість без зовнішніх елементів
        `,
        book: 'Clean Architecture - Robert C. Martin'
      },
      {
        id: 'boundaries',
        title: 'Architectural Boundaries',
        content: `
# Architectural Boundaries

Границі — це лінії, проведені між компонентами системи, що розділяють речі, які мають значення, від речей, які не мають.

## Типи границь

### 1. Локальні процеси
- Сервіси в одному процесі
- Комунікація через виклики функцій
- Найшвидший тип границі

### 2. Розгортання
- Окремі деплойменти
- Спільний адресний простір
- DLLs, JARs, shared libraries

### 3. Потоки
- Окремі потоки в процесі
- Асинхронна комунікація
- Message queues

### 4. Локальні сервіси
- Окремі процеси на одній машині
- Sockets, named pipes
- Середня затримка

### 5. Мережеві сервіси
- Окремі машини
- HTTP, gRPC, message brokers
- Найвища затримка

## Правило перетину границь
При перетині границі, дані повинні бути в простому форматі — примітиви або прості структури даних. Ніколи не передавайте Entity-об'єкти через границю!
        `,
        book: 'Clean Architecture - Robert C. Martin'
      }
    ]
  },
  {
    id: 'ddd',
    title: 'Domain-Driven Design',
    icon: GitBranch,
    color: 'from-purple-500 to-pink-500',
    description: 'Проектування на основі предметної області',
    topics: [
      {
        id: 'bounded-context',
        title: 'Bounded Context',
        content: `
# Bounded Context

Bounded Context — це центральний паттерн у Domain-Driven Design. Це явна межа, в межах якої модель домену визначена та застосовується.

## Навіщо потрібні Bounded Contexts?

У великих системах різні частини бізнесу мають різне розуміння одних і тих же понять:
- **Відділ продажів**: "Клієнт" — це потенційний покупець
- **Відділ підтримки**: "Клієнт" — це користувач з тікетами
- **Бухгалтерія**: "Клієнт" — це платник з рахунками

## Приклад: E-Commerce

\`\`\`
┌─────────────────┐  ┌─────────────────┐
│    Ordering     │  │   Inventory     │
│    Context      │  │    Context      │
│                 │  │                 │
│  - Order        │  │  - Product      │
│  - Customer     │  │  - Stock        │
│  - Payment      │  │  - Warehouse    │
└─────────────────┘  └─────────────────┘
         │                    │
         └────────┬───────────┘
                  │
          Integration Layer
\`\`\`

## Ключові концепції

### Ubiquitous Language
Кожен Bounded Context має свою "всюдисущу мову" — термінологію, зрозумілу всім учасникам (розробникам, бізнесу).

### Context Mapping
Візуалізація відносин між Bounded Contexts:
- **Partnership**: команди співпрацюють
- **Customer-Supplier**: один Context залежить від іншого
- **Conformist**: слідує моделі іншого Context
- **Anti-Corruption Layer**: захист від зовнішніх моделей
        `,
        book: 'Domain-Driven Design - Eric Evans'
      },
      {
        id: 'aggregates',
        title: 'Aggregates & Entities',
        content: `
# Aggregates & Entities

## Entity (Сутність)
Об'єкт з унікальною ідентичністю, яка зберігається протягом життя об'єкта.

\`\`\`python
class User:
    def __init__(self, user_id: str, email: str):
        self.id = user_id  # Ідентичність!
        self.email = email
    
    def __eq__(self, other):
        return self.id == other.id  # Порівняння за ID
\`\`\`

## Value Object (Об'єкт-значення)
Об'єкт без унікальної ідентичності, визначається своїми атрибутами.

\`\`\`python
@dataclass(frozen=True)
class Money:
    amount: Decimal
    currency: str
    
    def add(self, other: 'Money') -> 'Money':
        if self.currency != other.currency:
            raise ValueError("Currency mismatch")
        return Money(self.amount + other.amount, self.currency)
\`\`\`

## Aggregate (Агрегат)
Кластер пов'язаних об'єктів, що розглядаються як єдине ціле.

### Правила агрегатів:
1. **Aggregate Root** — єдина точка входу до агрегату
2. Зовнішні об'єкти можуть посилатися тільки на Root
3. Транзакційна консистентність в межах одного агрегату
4. Eventual consistency між агрегатами

\`\`\`python
class Order:  # Aggregate Root
    def __init__(self, order_id: str):
        self.id = order_id
        self._items: List[OrderItem] = []  # Частина агрегату
    
    def add_item(self, product_id: str, quantity: int):
        # Бізнес-правила тут!
        item = OrderItem(product_id, quantity)
        self._items.append(item)
\`\`\`
        `,
        book: 'Domain-Driven Design - Eric Evans'
      },
      {
        id: 'domain-events',
        title: 'Domain Events',
        content: `
# Domain Events

Domain Event — це щось, що сталося в домені і є важливим для бізнес-експертів.

## Характеристики Domain Events

- **Immutable**: події не змінюються після створення
- **Past tense**: назви у минулому часі (OrderPlaced, PaymentReceived)
- **Timestamped**: завжди містять час події
- **Self-contained**: містять всю необхідну інформацію

## Приклад реалізації

\`\`\`python
@dataclass(frozen=True)
class OrderPlaced:
    order_id: str
    customer_id: str
    total_amount: Money
    placed_at: datetime
    
class Order:
    def __init__(self):
        self._events = []
    
    def place(self, customer_id: str, items: List[OrderItem]):
        # Бізнес-логіка...
        total = self._calculate_total(items)
        
        # Створюємо подію
        self._events.append(OrderPlaced(
            order_id=self.id,
            customer_id=customer_id,
            total_amount=total,
            placed_at=datetime.now(timezone.utc)
        ))
    
    def collect_events(self) -> List:
        events = self._events.copy()
        self._events.clear()
        return events
\`\`\`

## Event-Driven Architecture

Domain Events — основа для:
- **Event Sourcing**: зберігання стану як послідовності подій
- **CQRS**: розділення read і write моделей
- **Saga Pattern**: координація між сервісами
- **Audit Logging**: автоматичний аудит змін
        `,
        book: 'Domain-Driven Design - Eric Evans'
      }
    ]
  },
  {
    id: 'microservices',
    title: 'Microservices',
    icon: Cloud,
    color: 'from-green-500 to-emerald-500',
    description: 'Паттерни та практики мікросервісної архітектури',
    topics: [
      {
        id: 'decomposition',
        title: 'Service Decomposition',
        content: `
# Service Decomposition

Як розбити монолітний додаток на мікросервіси?

## Стратегії декомпозиції

### 1. Decompose by Business Capability
Сервіси відповідають бізнес-можливостям організації.

\`\`\`
E-Commerce Platform:
├── Product Catalog Service
├── Order Management Service
├── Customer Service
├── Payment Service
├── Shipping Service
└── Notification Service
\`\`\`

### 2. Decompose by Subdomain (DDD)
Використовуйте Bounded Contexts як границі сервісів.

### 3. Decompose by Use Case
Кожен сервіс обслуговує конкретний варіант використання.

## Правила декомпозиції

### Single Responsibility
Кожен сервіс робить одну річ добре.

### Loose Coupling
Зміни в одному сервісі не повинні вимагати змін в інших.

### High Cohesion
Пов'язана функціональність згрупована разом.

## Антипаттерни

❌ **Distributed Monolith**: сервіси сильно зв'язані, деплояться разом
❌ **Nano-services**: занадто дрібне розбиття, overhead перевищує користь
❌ **Chatty Services**: забагато мережевих викликів між сервісами
        `,
        book: 'Microservices Patterns - Chris Richardson'
      },
      {
        id: 'saga-pattern',
        title: 'Saga Pattern',
        content: `
# Saga Pattern

Saga — це послідовність локальних транзакцій, де кожна транзакція оновлює дані в одному сервісі та публікує подію для запуску наступної.

## Проблема
Distributed transactions (2PC) погано масштабуються та знижують доступність системи.

## Рішення: Saga

### Choreography-based Saga
Сервіси координуються через події без центрального координатора.

\`\`\`
[Order Service] → OrderCreated
                      ↓
[Payment Service] → PaymentProcessed
                      ↓
[Inventory Service] → InventoryReserved
                      ↓
[Shipping Service] → ShipmentCreated
\`\`\`

**Переваги**: простота, loose coupling
**Недоліки**: важко відстежувати логіку

### Orchestration-based Saga
Центральний оркестратор керує всіма кроками.

\`\`\`
                    ┌──────────────────┐
                    │  Order Saga      │
                    │  Orchestrator    │
                    └────────┬─────────┘
           ┌─────────────────┼─────────────────┐
           ↓                 ↓                 ↓
    [Payment]          [Inventory]       [Shipping]
\`\`\`

**Переваги**: чітка логіка, легше відлагоджувати
**Недоліки**: single point of failure

## Compensating Transactions

Кожен крок має компенсуючу транзакцію для відкату:

| Step | Action | Compensation |
|------|--------|--------------|
| 1 | Create Order | Cancel Order |
| 2 | Reserve Credit | Release Credit |
| 3 | Reserve Inventory | Release Inventory |
        `,
        book: 'Microservices Patterns - Chris Richardson'
      },
      {
        id: 'api-gateway',
        title: 'API Gateway Pattern',
        content: `
# API Gateway Pattern

API Gateway — єдина точка входу для всіх клієнтів до мікросервісної системи.

## Функції API Gateway

### 1. Request Routing
Маршрутизація запитів до відповідних сервісів.

### 2. API Composition
Агрегація даних з кількох сервісів в один запит.

\`\`\`
Client Request: GET /product/123

API Gateway:
├── GET /catalog/products/123
├── GET /inventory/stock/123
├── GET /reviews/product/123
└── Compose Response
\`\`\`

### 3. Protocol Translation
HTTP → gRPC, REST → GraphQL, etc.

### 4. Cross-cutting Concerns
- **Authentication/Authorization**
- **Rate Limiting**
- **Caching**
- **Logging & Monitoring**
- **Request/Response transformation**

## Backend for Frontend (BFF)

Окремий API Gateway для кожного типу клієнта:

\`\`\`
┌─────────┐  ┌─────────┐  ┌─────────┐
│ Mobile  │  │   Web   │  │ Partner │
│   App   │  │   App   │  │   API   │
└────┬────┘  └────┬────┘  └────┬────┘
     ↓            ↓            ↓
┌─────────┐  ┌─────────┐  ┌─────────┐
│ Mobile  │  │   Web   │  │ Public  │
│   BFF   │  │   BFF   │  │   BFF   │
└────┬────┘  └────┬────┘  └────┬────┘
     └────────────┼────────────┘
                  ↓
          [Microservices]
\`\`\`

## Популярні реалізації
- Kong
- AWS API Gateway
- Netflix Zuul
- Nginx
- Traefik
        `,
        book: 'Microservices Patterns - Chris Richardson'
      }
    ]
  },
  {
    id: 'scalability',
    title: 'Scalability & Performance',
    icon: Cpu,
    color: 'from-orange-500 to-red-500',
    description: 'Масштабування та оптимізація продуктивності',
    topics: [
      {
        id: 'caching',
        title: 'Caching Strategies',
        content: `
# Caching Strategies

Кешування — одна з найефективніших технік оптимізації продуктивності.

## Типи кешів

### 1. Application Cache
Redis, Memcached — швидкий доступ до часто запитуваних даних.

### 2. Database Query Cache
MySQL Query Cache, PostgreSQL prepared statements.

### 3. CDN (Content Delivery Network)
Географічно розподілений кеш для статичних файлів.

### 4. Browser Cache
HTTP cache headers для клієнтського кешування.

## Cache Patterns

### Cache-Aside (Lazy Loading)
\`\`\`python
def get_user(user_id):
    # 1. Перевірити кеш
    user = cache.get(f"user:{user_id}")
    if user:
        return user
    
    # 2. Завантажити з БД
    user = db.query("SELECT * FROM users WHERE id = ?", user_id)
    
    # 3. Зберегти в кеш
    cache.set(f"user:{user_id}", user, ttl=3600)
    return user
\`\`\`

### Write-Through
Кожен запис спочатку йде в кеш, потім в БД.

### Write-Behind (Write-Back)
Записи буферизуються в кеші і періодично синхронізуються з БД.

## Cache Invalidation

> "There are only two hard things in Computer Science: cache invalidation and naming things." — Phil Karlton

### Стратегії інвалідації:
1. **TTL (Time-To-Live)**: автоматичне видалення через час
2. **Event-based**: інвалідація при зміні даних
3. **Version-based**: версіонування ключів кешу
        `,
        book: 'Designing Data-Intensive Applications - Martin Kleppmann'
      },
      {
        id: 'database-scaling',
        title: 'Database Scaling',
        content: `
# Database Scaling

## Vertical Scaling (Scale Up)
Збільшення ресурсів одного сервера: CPU, RAM, SSD.

**Переваги**: простота
**Недоліки**: ліміти hardware, single point of failure

## Horizontal Scaling (Scale Out)

### Read Replicas
\`\`\`
        ┌─────────────┐
        │   Primary   │ ← Writes
        └──────┬──────┘
               │ Replication
    ┌──────────┼──────────┐
    ↓          ↓          ↓
┌───────┐  ┌───────┐  ┌───────┐
│Replica│  │Replica│  │Replica│ ← Reads
└───────┘  └───────┘  └───────┘
\`\`\`

### Sharding (Partitioning)
Розподіл даних між кількома базами.

**Стратегії шардингу:**
- **Range-based**: за діапазоном значень (user_id 1-1M → shard1)
- **Hash-based**: hash(user_id) % num_shards
- **Geographic**: за географічним розташуванням

\`\`\`
User ID → Hash Function → Shard Selection
   ↓
┌──────┐  ┌──────┐  ┌──────┐  ┌──────┐
│Shard1│  │Shard2│  │Shard3│  │Shard4│
└──────┘  └──────┘  └──────┘  └──────┘
\`\`\`

## CAP Theorem

У розподіленій системі можна гарантувати лише 2 з 3:
- **Consistency**: всі вузли бачать однакові дані
- **Availability**: система відповідає на запити
- **Partition Tolerance**: система працює при мережевих збоях

**CP Systems**: MongoDB, HBase, Redis Cluster
**AP Systems**: Cassandra, DynamoDB, CouchDB
        `,
        book: 'Designing Data-Intensive Applications - Martin Kleppmann'
      },
      {
        id: 'load-balancing',
        title: 'Load Balancing',
        content: `
# Load Balancing

Load Balancer розподіляє трафік між кількома серверами.

## Алгоритми балансування

### Round Robin
Послідовний розподіл запитів по черзі.

### Weighted Round Robin
Сервери з більшою вагою отримують більше запитів.

### Least Connections
Запит йде до сервера з найменшою кількістю активних з'єднань.

### IP Hash
Один клієнт завжди потрапляє на один сервер (sticky sessions).

### Least Response Time
Запит йде до найшвидшого сервера.

## Типи Load Balancers

### Layer 4 (Transport)
Працює на рівні TCP/UDP. Швидкий, але "сліпий" до контенту.

### Layer 7 (Application)
Працює на рівні HTTP. Може маршрутизувати за URL, headers, cookies.

\`\`\`
                    ┌─────────────────┐
    Requests  →     │  Load Balancer  │
                    └────────┬────────┘
               ┌─────────────┼─────────────┐
               ↓             ↓             ↓
          ┌────────┐    ┌────────┐    ┌────────┐
          │Server 1│    │Server 2│    │Server 3│
          └────────┘    └────────┘    └────────┘
\`\`\`

## Health Checks

Load Balancer перевіряє здоров'я серверів:
- **Passive**: аналіз відповідей на реальні запити
- **Active**: періодичні ping/health endpoints

## Популярні рішення
- **Hardware**: F5, Citrix
- **Software**: Nginx, HAProxy, Traefik
- **Cloud**: AWS ALB/NLB, GCP Load Balancing, Azure Load Balancer
        `,
        book: 'Designing Data-Intensive Applications - Martin Kleppmann'
      }
    ]
  },
  {
    id: 'resilience',
    title: 'Resilience Patterns',
    icon: Shield,
    color: 'from-indigo-500 to-violet-500',
    description: 'Паттерни відмовостійкості та надійності',
    topics: [
      {
        id: 'circuit-breaker',
        title: 'Circuit Breaker',
        content: `
# Circuit Breaker Pattern

Circuit Breaker запобігає каскадним відмовам у розподілених системах.

## Проблема
Коли сервіс недоступний, повторні запити:
- Витрачають ресурси
- Збільшують latency
- Можуть призвести до каскадних відмов

## Рішення: Circuit Breaker

### Стани Circuit Breaker

\`\`\`
        ┌──────────┐
   ┌───→│  CLOSED  │←──────────────────┐
   │    └────┬─────┘                   │
   │         │ Failures exceed        │
   │         │ threshold              │ Success
   │         ↓                        │ threshold
   │    ┌──────────┐                  │
   │    │   OPEN   │                  │
   │    └────┬─────┘                  │
   │         │ Timeout                │
   │         ↓                        │
   │    ┌──────────┐                  │
   └────│HALF-OPEN │──────────────────┘
        └──────────┘
\`\`\`

### Реалізація

\`\`\`python
class CircuitBreaker:
    def __init__(self, failure_threshold=5, timeout=30):
        self.failure_threshold = failure_threshold
        self.timeout = timeout
        self.failures = 0
        self.state = "CLOSED"
        self.last_failure_time = None
    
    def call(self, func, *args, **kwargs):
        if self.state == "OPEN":
            if time.time() - self.last_failure_time > self.timeout:
                self.state = "HALF_OPEN"
            else:
                raise CircuitOpenError("Circuit is OPEN")
        
        try:
            result = func(*args, **kwargs)
            self._on_success()
            return result
        except Exception as e:
            self._on_failure()
            raise
    
    def _on_success(self):
        self.failures = 0
        self.state = "CLOSED"
    
    def _on_failure(self):
        self.failures += 1
        self.last_failure_time = time.time()
        if self.failures >= self.failure_threshold:
            self.state = "OPEN"
\`\`\`

## Бібліотеки
- **Python**: pybreaker, circuitbreaker
- **Java**: Resilience4j, Hystrix (deprecated)
- **Go**: sony/gobreaker
        `,
        book: 'Release It! - Michael Nygard'
      },
      {
        id: 'retry-pattern',
        title: 'Retry Pattern',
        content: `
# Retry Pattern

Автоматичне повторення операцій при тимчасових збоях.

## Коли використовувати Retry

✅ **Тимчасові помилки:**
- Мережеві таймаути
- 503 Service Unavailable
- Database connection errors
- Rate limiting (429)

❌ **Постійні помилки (не повторювати):**
- 400 Bad Request
- 401 Unauthorized
- 404 Not Found
- Business logic errors

## Стратегії Retry

### Immediate Retry
Негайне повторення — рідко ефективно.

### Fixed Delay
\`\`\`python
for attempt in range(max_retries):
    try:
        return make_request()
    except TransientError:
        time.sleep(1)  # Фіксована затримка
\`\`\`

### Exponential Backoff
\`\`\`python
for attempt in range(max_retries):
    try:
        return make_request()
    except TransientError:
        delay = base_delay * (2 ** attempt)
        time.sleep(delay)
\`\`\`

### Exponential Backoff with Jitter
Додаємо випадковість для уникнення "thundering herd":

\`\`\`python
import random

for attempt in range(max_retries):
    try:
        return make_request()
    except TransientError:
        delay = base_delay * (2 ** attempt)
        jitter = random.uniform(0, delay * 0.1)
        time.sleep(delay + jitter)
\`\`\`

## Бібліотеки
- **Python**: tenacity, backoff
- **Java**: Resilience4j, Spring Retry
- **JavaScript**: async-retry, p-retry
        `,
        book: 'Release It! - Michael Nygard'
      },
      {
        id: 'bulkhead',
        title: 'Bulkhead Pattern',
        content: `
# Bulkhead Pattern

Bulkhead ізолює компоненти системи для запобігання каскадним відмовам.

## Аналогія
Як перегородки в кораблі — якщо один відсік затоплюється, інші залишаються сухими.

## Типи Bulkhead

### Thread Pool Isolation
Окремі пули потоків для різних операцій.

\`\`\`python
# Пул для критичних операцій
critical_executor = ThreadPoolExecutor(max_workers=10)

# Пул для некритичних операцій  
background_executor = ThreadPoolExecutor(max_workers=5)

# Якщо background_executor перевантажений,
# critical_executor продовжує працювати
\`\`\`

### Semaphore Isolation
Обмеження кількості одночасних операцій.

\`\`\`python
import asyncio

payment_semaphore = asyncio.Semaphore(100)
notification_semaphore = asyncio.Semaphore(50)

async def process_payment():
    async with payment_semaphore:
        # Максимум 100 одночасних платежів
        await external_payment_service()

async def send_notification():
    async with notification_semaphore:
        # Максимум 50 одночасних нотифікацій
        await notification_service()
\`\`\`

### Connection Pool Isolation
Окремі пули з'єднань для різних сервісів.

\`\`\`
┌──────────────────────────────────┐
│           Application            │
├─────────┬─────────┬──────────────┤
│ Pool A  │ Pool B  │   Pool C     │
│ (DB)    │ (Cache) │   (API)      │
│ max=20  │ max=50  │   max=10     │
└────┬────┴────┬────┴───────┬──────┘
     ↓         ↓            ↓
  Database   Redis     External API
\`\`\`

## Переваги
- Ізоляція відмов
- Передбачувана поведінка під навантаженням
- Захист критичних операцій
        `,
        book: 'Release It! - Michael Nygard'
      }
    ]
  },
  {
    id: 'data-intensive',
    title: 'Data-Intensive Apps',
    icon: Database,
    color: 'from-yellow-500 to-amber-500',
    description: 'Проектування систем з великими даними',
    topics: [
      {
        id: 'replication',
        title: 'Replication',
        content: `
# Database Replication

Реплікація — збереження копій даних на кількох машинах.

## Цілі реплікації
- **High Availability**: система працює при виході вузлів
- **Scalability**: розподіл read-навантаження
- **Latency**: дані ближче до користувачів

## Моделі реплікації

### Single-Leader (Master-Slave)
\`\`\`
Writes → [Leader] → Replication → [Follower 1]
                  ↘             → [Follower 2]
                                → [Follower 3]
         Reads ←────────────────────────────────
\`\`\`

**Переваги**: простота, консистентність writes
**Недоліки**: leader — single point of failure

### Multi-Leader
Кілька вузлів приймають writes.

**Use cases**: 
- Multi-datacenter
- Offline-first apps
- Collaborative editing

**Проблема**: конфлікти при одночасних writes

### Leaderless (Dynamo-style)
Будь-який вузол приймає reads і writes.

\`\`\`
Client → Write to 3 nodes (W=3)
      → Read from 2 nodes (R=2)
      
R + W > N guarantees consistency
(2 + 3 > 3) ✓
\`\`\`

## Синхронна vs Асинхронна реплікація

| | Синхронна | Асинхронна |
|---|---|---|
| Консистентність | Сильна | Eventual |
| Latency | Вища | Нижча |
| Доступність | Нижча | Вища |
        `,
        book: 'Designing Data-Intensive Applications - Martin Kleppmann'
      },
      {
        id: 'partitioning',
        title: 'Partitioning',
        content: `
# Data Partitioning (Sharding)

Partitioning — розподіл великого набору даних між кількома вузлами.

## Стратегії партиціонування

### Key Range Partitioning
\`\`\`
Partition 1: A-F
Partition 2: G-L
Partition 3: M-R
Partition 4: S-Z
\`\`\`

**Переваги**: range queries ефективні
**Недоліки**: hot spots при нерівномірних даних

### Hash Partitioning
\`\`\`
partition = hash(key) % num_partitions
\`\`\`

**Переваги**: рівномірний розподіл
**Недоліки**: range queries неефективні

### Consistent Hashing
Мінімізує перерозподіл даних при додаванні/видаленні вузлів.

\`\`\`
      Node A
        │
   ┌────┼────┐
   │    │    │
Node D──●────Node B
   │    │    │
   └────┼────┘
        │
      Node C
\`\`\`

## Secondary Indexes

### Local Index (Document-partitioned)
Кожна партиція має свій індекс.
- Write: оновлюємо одну партицію
- Read: scatter-gather по всіх партиціях

### Global Index (Term-partitioned)
Індекс також партиціонований.
- Write: оновлюємо кілька партицій
- Read: одна партиція

## Rebalancing

Перерозподіл даних при:
- Додаванні нових вузлів
- Виході вузлів з ладу
- Нерівномірному навантаженні
        `,
        book: 'Designing Data-Intensive Applications - Martin Kleppmann'
      },
      {
        id: 'consistency',
        title: 'Consistency Models',
        content: `
# Consistency Models

## Strong Consistency
Всі клієнти бачать однакові дані в будь-який момент.

\`\`\`
Write(x=1) → [Database] → Read(x) returns 1
                        → Read(x) returns 1
                        → Read(x) returns 1
\`\`\`

## Eventual Consistency
Дані стануть консистентними "з часом".

\`\`\`
Write(x=1) → [Database] → Read(x) returns 0 (stale)
           → Replication... 
                        → Read(x) returns 1 (eventually)
\`\`\`

## Causal Consistency
Зберігає причинно-наслідкові зв'язки.

\`\`\`
User A: Write("Hello") → ID=1
User B: Reply("Hi", reply_to=1) → ID=2

Читач завжди бачить #1 перед #2
\`\`\`

## Read Your Writes
Користувач завжди бачить свої власні записи.

## Monotonic Reads
Якщо побачив нове значення, не побачиш старе.

## Linearizability
Найсильніша гарантія — система поводиться як єдина копія даних.

**Реалізація**: 
- Single-leader з синхронною реплікацією
- Consensus алгоритми (Raft, Paxos)

**Вартість**: 
- Вища latency
- Нижча availability при network partitions
        `,
        book: 'Designing Data-Intensive Applications - Martin Kleppmann'
      }
    ]
  }
];

export default function LearningMode() {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [completedTopics, setCompletedTopics] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProgress();
  }, []);

  const fetchProgress = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/learning/progress`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setCompletedTopics(data.completed_topics || []);
      }
    } catch (error) {
      console.error('Error fetching progress:', error);
    } finally {
      setLoading(false);
    }
  };

  const markTopicComplete = async (topicId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/learning/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ topic_id: topicId })
      });
      
      if (response.ok) {
        const data = await response.json();
        setCompletedTopics(prev => [...prev, topicId]);
        toast.success(`+${data.xp_earned} XP за вивчення теми!`);
      }
    } catch (error) {
      console.error('Error marking complete:', error);
    }
  };

  const filteredCategories = LEARNING_CATEGORIES.filter(cat =>
    cat.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    cat.topics.some(t => t.title.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const totalTopics = LEARNING_CATEGORIES.reduce((sum, cat) => sum + cat.topics.length, 0);
  const completedCount = completedTopics.length;
  const progressPercent = Math.round((completedCount / totalTopics) * 100);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Topic Detail View
  if (selectedTopic) {
    const isCompleted = completedTopics.includes(selectedTopic.id);
    
    return (
      <div className="min-h-screen bg-background">
        <div className="grid-background min-h-screen">
          <nav className="border-b border-border/50 backdrop-blur-sm bg-background/80 sticky top-0 z-10">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-center justify-between h-16">
                <Button
                  variant="ghost"
                  onClick={() => setSelectedTopic(null)}
                  data-testid="back-to-category"
                  className="flex items-center space-x-2"
                >
                  <ArrowLeft className="w-5 h-5" />
                  <span>Назад</span>
                </Button>
                <h1 className="text-lg font-bold text-foreground truncate max-w-xs">
                  {selectedTopic.title}
                </h1>
                <div className="w-24"></div>
              </div>
            </div>
          </nav>

          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {/* Topic Header */}
              <div className="mb-8">
                <div className="flex items-center space-x-3 mb-4">
                  {isCompleted && (
                    <span className="flex items-center px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm">
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Вивчено
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground flex items-center">
                  <BookOpen className="w-4 h-4 mr-2" />
                  {selectedTopic.book}
                </p>
              </div>

              {/* Topic Content */}
              <div className="bg-card/50 backdrop-blur-sm border border-white/5 rounded-xl p-6 md:p-8">
                <MarkdownContent content={selectedTopic.content} />
              </div>

              {/* Interactive Diagram */}
              {TOPIC_DIAGRAMS[selectedTopic.id] && (
                <div className="mt-8">
                  <div className="flex items-center gap-2 mb-4">
                    <Play className="w-5 h-5 text-primary" />
                    <h3 className="text-xl font-bold text-foreground">Інтерактивна Візуалізація</h3>
                  </div>
                  {(() => {
                    const DiagramComponent = TOPIC_DIAGRAMS[selectedTopic.id];
                    return <DiagramComponent />;
                  })()}
                </div>
              )}

              {/* Action Buttons */}
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                {!isCompleted ? (
                  <Button
                    onClick={() => markTopicComplete(selectedTopic.id)}
                    className="flex-1 bg-gradient-to-r from-primary to-secondary hover:opacity-90"
                    data-testid="mark-complete-button"
                  >
                    <CheckCircle className="w-5 h-5 mr-2" />
                    Позначити як вивчене (+15 XP)
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    className="flex-1"
                    disabled
                  >
                    <CheckCircle className="w-5 h-5 mr-2 text-green-400" />
                    Вже вивчено
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={() => setSelectedTopic(null)}
                  className="flex-1"
                >
                  Повернутися до списку
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    );
  }

  // Category Topics View
  if (selectedCategory) {
    return (
      <div className="min-h-screen bg-background">
        <div className="grid-background min-h-screen">
          <nav className="border-b border-border/50 backdrop-blur-sm bg-background/80">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-center justify-between h-16">
                <Button
                  variant="ghost"
                  onClick={() => setSelectedCategory(null)}
                  data-testid="back-to-categories"
                  className="flex items-center space-x-2"
                >
                  <ArrowLeft className="w-5 h-5" />
                  <span>Назад</span>
                </Button>
                <h1 className="text-xl font-bold text-foreground">{selectedCategory.title}</h1>
                <div className="w-24"></div>
              </div>
            </div>
          </nav>

          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <p className="text-muted-foreground mb-8">{selectedCategory.description}</p>

              <div className="space-y-4">
                {selectedCategory.topics.map((topic, idx) => {
                  const isCompleted = completedTopics.includes(topic.id);
                  return (
                    <motion.div
                      key={topic.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.1 }}
                      onClick={() => setSelectedTopic(topic)}
                      className={`bg-card/50 backdrop-blur-sm border p-6 rounded-xl cursor-pointer transition-all hover:border-primary/50 hover:-translate-y-1 ${
                        isCompleted ? 'border-green-500/30' : 'border-white/5'
                      }`}
                      data-testid={`topic-${topic.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          {isCompleted ? (
                            <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                              <CheckCircle className="w-5 h-5 text-green-400" />
                            </div>
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                              <BookOpen className="w-5 h-5 text-primary" />
                            </div>
                          )}
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="text-lg font-bold text-foreground">{topic.title}</h3>
                              {TOPIC_DIAGRAMS[topic.id] && (
                                <span className="px-2 py-0.5 text-xs bg-primary/20 text-primary rounded-full flex items-center gap-1">
                                  <Play className="w-3 h-3" />
                                  Інтерактив
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">{topic.book}</p>
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-muted-foreground" />
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    );
  }

  // Main Categories View
  return (
    <div className="min-h-screen bg-background">
      <div className="grid-background min-h-screen">
        <nav className="border-b border-border/50 backdrop-blur-sm bg-background/80">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Button
                variant="ghost"
                onClick={() => navigate('/dashboard')}
                data-testid="back-to-dashboard"
                className="flex items-center space-x-2"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Назад</span>
              </Button>
              <h1 className="text-xl font-bold text-foreground">Режим Навчання</h1>
              <div className="w-24"></div>
            </div>
          </div>
        </nav>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Header */}
            <div className="text-center mb-8">
              <BookOpen className="w-16 h-16 text-primary mx-auto mb-4" />
              <h2 className="text-4xl font-black text-foreground mb-2">База Знань Архітектора</h2>
              <p className="text-lg text-muted-foreground">
                Вивчайте архітектурні концепції з найкращих книг індустрії
              </p>
            </div>

            {/* Progress */}
            <div className="bg-card/50 backdrop-blur-sm border border-white/5 p-6 rounded-xl mb-8">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-bold text-foreground">Ваш прогрес</h3>
                  <p className="text-sm text-muted-foreground">
                    Вивчено {completedCount} з {totalTopics} тем
                  </p>
                </div>
                <div className="text-3xl font-bold text-primary">{progressPercent}%</div>
              </div>
              <div className="w-full bg-background rounded-full h-3">
                <div 
                  className="bg-gradient-to-r from-primary to-secondary h-3 rounded-full transition-all duration-500"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>

            {/* Search */}
            <div className="relative mb-8">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Пошук тем..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 bg-card/50 border-white/10"
                data-testid="search-input"
              />
            </div>

            {/* Categories Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredCategories.map((category, idx) => {
                const categoryCompleted = category.topics.filter(t => 
                  completedTopics.includes(t.id)
                ).length;
                const categoryProgress = Math.round((categoryCompleted / category.topics.length) * 100);
                
                return (
                  <motion.div
                    key={category.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.1 }}
                    onClick={() => setSelectedCategory(category)}
                    className="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/5 p-6 hover:border-primary/50 transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                    data-testid={`category-${category.id}`}
                  >
                    <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${category.color} opacity-20 blur-3xl group-hover:opacity-30 transition-opacity`}></div>
                    
                    <div className="relative z-10">
                      <div className="flex items-center justify-between mb-4">
                        <div className="w-14 h-14 rounded-xl bg-primary/20 flex items-center justify-center group-hover:bg-primary/30 transition-colors">
                          <category.icon className="w-7 h-7 text-primary" />
                        </div>
                        {categoryProgress === 100 && (
                          <Star className="w-6 h-6 text-yellow-400" />
                        )}
                      </div>
                      
                      <h3 className="text-xl font-bold text-foreground mb-2">{category.title}</h3>
                      <p className="text-muted-foreground text-sm mb-4">{category.description}</p>
                      
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">
                          {categoryCompleted}/{category.topics.length} тем
                        </span>
                        <span className="text-primary font-medium">
                          {categoryProgress}%
                        </span>
                      </div>
                      
                      <div className="mt-2 w-full bg-background rounded-full h-2">
                        <div 
                          className={`bg-gradient-to-r ${category.color} h-2 rounded-full transition-all duration-500`}
                          style={{ width: `${categoryProgress}%` }}
                        />
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Books Reference */}
            <div className="mt-12">
              <h3 className="text-xl font-bold text-foreground mb-4">Джерела знань</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { title: 'Clean Architecture', author: 'Robert C. Martin' },
                  { title: 'Domain-Driven Design', author: 'Eric Evans' },
                  { title: 'Microservices Patterns', author: 'Chris Richardson' },
                  { title: 'Designing Data-Intensive Applications', author: 'Martin Kleppmann' },
                  { title: 'Release It!', author: 'Michael Nygard' },
                  { title: 'Building Evolutionary Architectures', author: 'Neal Ford' }
                ].map((book, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center space-x-3 bg-card/30 p-4 rounded-lg border border-white/5"
                  >
                    <BookOpen className="w-5 h-5 text-primary" />
                    <div>
                      <p className="font-medium text-foreground">{book.title}</p>
                      <p className="text-sm text-muted-foreground">{book.author}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
        <Footer />
      </div>
    </div>
  );
}
